/* eslint-disable max-lines */

import { parsePartialJson, type UIMessage } from '@ai-sdk/ui-utils';

import { generateUuid } from '@core/utils-legacy';

import type {
  AccumulatorToolPart,
  Chunk,
  MessageBuffer,
  Part,
  ReasoningPayload,
  RenderComponentData,
  RenderComponentPart,
  SandboxToolCallData,
  SandboxToolCallPart,
  TextPart,
  TextPayload,
  ToolCallDeltaPayload,
  ToolCallFinalPayload,
  ToolCallStartPayload,
  ToolOutputPayload,
  ToolPart,
  ToolPartUpsert,
  ToolResultPayload,
} from './accumulator.types';

export type { Chunk } from './accumulator.types';

const SUB_AGENT_TOOL_NAME_PREFIX = 'agent-';

/**
 * Folds Mastra SSE chunks into a growing `UIMessage[]`.
 *
 * One instance per active stream. `apply()` is called for each incoming chunk;
 * `messages` returns the current rolled-up state. Sub-agent (`agent-*`) tool
 * calls accumulate into a nested buffer attached to the parent tool-call part
 * (see `subAgentBuffer` in the internal types).
 */
export class MastraStreamAccumulator {
  private buffer: MessageBuffer;

  constructor(initial: readonly UIMessage[] = []) {
    this.buffer = this.createBuffer();
    this.buffer.messages = [...initial];
  }

  /** Snapshot of the accumulated messages. Treat as read-only. */
  public get messages(): readonly UIMessage[] {
    return this.buffer.messages;
  }

  /** Apply a single SSE chunk. Returns the post-apply snapshot. */
  public apply(input: { type: string; payload?: unknown }): readonly UIMessage[] {
    this.applyChunk(this.buffer, <Chunk>input);
    return this.buffer.messages;
  }

  /** Replace the buffered messages (used to seed from history before resuming a stream). */
  public setMessages(messages: readonly UIMessage[]): void {
    this.buffer.messages = [...messages];
  }

  /** Drop all buffered messages and partial-args state. */
  public reset(): void {
    this.buffer.messages = [];
    this.buffer.partialArgs.clear();
  }

  public beginAssistantTurn(): void {
    if (this.hasTrailingEmptyAssistant()) return;
    this.appendAssistantMessage(this.buffer, generateUuid());
  }

  public pruneTrailingEmptyAssistant(): void {
    if (!this.hasTrailingEmptyAssistant()) return;
    this.buffer.messages = this.buffer.messages.slice(0, -1);
  }

  private createBuffer(): MessageBuffer {
    return { messages: [], partialArgs: new Map() };
  }

  private applyChunk(buffer: MessageBuffer, chunk: Chunk): void {
    switch (chunk.type) {
      case 'start':
        this.startAssistantMessage(buffer, chunk.payload?.messageId ?? generateUuid());
        break;
      case 'text-start':
      case 'text-delta':
      case 'text-end':
        this.applyTextChunk(buffer, chunk.type, chunk.payload);
        break;
      case 'reasoning-delta':
        this.applyReasoningDelta(buffer, chunk.payload);
        break;
      case 'tool-call-input-streaming-start':
        this.applyToolCallStart(buffer, chunk.payload);
        break;
      case 'tool-call-delta':
        this.applyToolCallDelta(buffer, chunk.payload);
        break;
      case 'tool-call':
        this.applyToolCallFinal(buffer, chunk.payload);
        break;
      case 'tool-result':
        this.applyToolResult(buffer, chunk.payload);
        break;
      case 'tool-output':
        this.applyToolOutput(buffer, chunk.payload);
        break;
      case 'data-render-component':
        this.applyRenderComponent(buffer, chunk.payload ?? chunk.data);
        break;
      case 'data-sandbox-tool-call':
        this.applySandboxToolCall(buffer, chunk.payload ?? chunk.data);
        break;
      case 'tool-call-input-streaming-end':
      case 'step-start':
      case 'step-finish':
      case 'finish':
      case 'error':
      default:
        // `tool-call-input-streaming-end` no-op: final args land on `tool-call`.
        break;
    }
  }

  private appendAssistantMessage(buffer: MessageBuffer, id: string): UIMessage {
    const message: UIMessage = { id, role: 'assistant', content: '', parts: [] };
    buffer.messages.push(message);
    return message;
  }

  private startAssistantMessage(buffer: MessageBuffer, id: string): UIMessage {
    const last = buffer.messages[buffer.messages.length - 1];
    if (last && last.role === 'assistant' && (last.parts?.length ?? 0) === 0) {
      return last;
    }
    return this.appendAssistantMessage(buffer, id);
  }

  private hasTrailingEmptyAssistant(): boolean {
    const last = this.buffer.messages[this.buffer.messages.length - 1];
    return !!last && last.role === 'assistant' && (last.parts?.length ?? 0) === 0;
  }

  private lastAssistantMessage(buffer: MessageBuffer): UIMessage {
    for (let index = buffer.messages.length - 1; index >= 0; index--) {
      const message = buffer.messages[index]!;
      if (message.role === 'assistant') return message;
    }
    return this.appendAssistantMessage(buffer, generateUuid());
  }

  private applyTextChunk(
    buffer: MessageBuffer,
    chunkType: 'text-start' | 'text-delta' | 'text-end',
    payload: TextPayload | undefined,
  ): void {
    const message = this.lastAssistantMessage(buffer);
    const parts = (message.parts ??= []);
    const partId = payload?.id ?? '';
    const isMatchingText = (part: Part): part is TextPart =>
      part.type === 'text' && (<TextPart>part).id === partId;
    let textPart = parts.find(isMatchingText);
    if (!textPart) {
      textPart = { type: 'text', text: '', id: partId };
      parts.push(textPart);
    }
    if (chunkType === 'text-delta') {
      textPart.text += payload?.text ?? '';
    }
    message.content = parts
      .filter((part): part is TextPart => part.type === 'text')
      .map(part => part.text)
      .join('');
  }

  private applyReasoningDelta(buffer: MessageBuffer, payload: ReasoningPayload | undefined): void {
    if (!payload?.text) return;
    const message = this.lastAssistantMessage(buffer);
    const parts = (message.parts ??= []);
    const lastPart = parts[parts.length - 1];
    let reasoningPart = lastPart?.type === 'reasoning' ? lastPart : undefined;
    if (!reasoningPart) {
      reasoningPart = { type: 'reasoning', reasoning: '', details: [] };
      parts.push(reasoningPart);
    }
    reasoningPart.reasoning += payload.text;
  }

  private applyToolCallStart(
    buffer: MessageBuffer,
    payload: ToolCallStartPayload | undefined,
  ): void {
    if (!payload) return;
    buffer.partialArgs.set(payload.toolCallId, { text: '', toolName: payload.toolName });
    this.upsertToolPart(buffer, {
      toolCallId: payload.toolCallId,
      toolName: payload.toolName,
      args: undefined,
      state: 'partial-call',
      providerMetadata: payload.providerMetadata,
    });
  }

  private applyToolCallDelta(
    buffer: MessageBuffer,
    payload: ToolCallDeltaPayload | undefined,
  ): void {
    if (!payload) return;
    const partial = buffer.partialArgs.get(payload.toolCallId);
    if (!partial) return;
    partial.text += payload.argsTextDelta ?? '';
    const { value: parsedArgs } = parsePartialJson(partial.text);
    this.upsertToolPart(buffer, {
      toolCallId: payload.toolCallId,
      toolName: partial.toolName,
      args: parsedArgs,
      state: 'partial-call',
      providerMetadata: payload.providerMetadata,
    });
  }

  private applyToolCallFinal(
    buffer: MessageBuffer,
    payload: ToolCallFinalPayload | undefined,
  ): void {
    if (!payload) return;
    buffer.partialArgs.delete(payload.toolCallId);
    this.upsertToolPart(buffer, {
      toolCallId: payload.toolCallId,
      toolName: payload.toolName,
      args: payload.args,
      state: 'call',
      providerMetadata: payload.providerMetadata,
    });
  }

  private applyToolResult(buffer: MessageBuffer, payload: ToolResultPayload | undefined): void {
    if (!payload) return;
    this.upsertToolPart(buffer, {
      toolCallId: payload.toolCallId,
      toolName: payload.toolName ?? '',
      args: payload.args,
      state: 'result',
      result: payload.result ?? payload.output,
      providerMetadata: payload.providerMetadata,
    });
  }

  private applyRenderComponent(buffer: MessageBuffer, data: RenderComponentData | undefined): void {
    if (!data || typeof data.componentId !== 'string') return;
    const message = this.lastAssistantMessage(buffer);
    const parts = (message.parts ??= []);
    const existing = parts.find(part => {
      const candidate = <RenderComponentPart>(<unknown>part);
      return (
        candidate.type === 'data-render-component' &&
        candidate.data?.componentId === data.componentId
      );
    });
    if (existing) {
      (<RenderComponentPart>(<unknown>existing)).data = data;
      return;
    }
    const part: RenderComponentPart = {
      type: 'data-render-component',
      data: { componentId: data.componentId, name: data.name, props: data.props },
    };
    parts.push(<Part>(<unknown>part));
  }

  private applySandboxToolCall(buffer: MessageBuffer, data: SandboxToolCallData | undefined): void {
    if (!data || typeof data.callId !== 'string') return;
    const message = this.lastAssistantMessage(buffer);
    const parts = (message.parts ??= []);
    // Same callId folds running → terminal into one row (and dedups stream replay).
    const existing = parts.find(part => {
      const candidate = <SandboxToolCallPart>(<unknown>part);
      return candidate.type === 'data-sandbox-tool-call' && candidate.data?.callId === data.callId;
    });
    if (existing) {
      (<SandboxToolCallPart>(<unknown>existing)).data = data;
      return;
    }
    // Append in arrival order: running emits at call start, so that is the intended order.
    const newPart: SandboxToolCallPart = {
      type: 'data-sandbox-tool-call',
      data: { callId: data.callId, id: data.id, status: data.status, durationMs: data.durationMs },
    };
    parts.push(<Part>(<unknown>newPart));
  }

  private applyToolOutput(buffer: MessageBuffer, payload: ToolOutputPayload | undefined): void {
    if (!payload) return;
    if (!payload.toolName?.startsWith(SUB_AGENT_TOOL_NAME_PREFIX)) return;
    const part = this.findToolInvocationByCallId(buffer, payload.toolCallId);
    if (!part) return;
    const sub = (part.subAgentBuffer ??= this.createBuffer());
    this.applyChunk(sub, <Chunk>payload.output);
  }

  private findToolInvocationByCallId(
    buffer: MessageBuffer,
    toolCallId: string,
  ): AccumulatorToolPart | undefined {
    for (let i = buffer.messages.length - 1; i >= 0; i--) {
      const msg = buffer.messages[i]!;
      if (msg.role !== 'assistant') continue;
      for (const part of msg.parts ?? []) {
        if (part.type !== 'tool-invocation') continue;
        const accPart = <AccumulatorToolPart>part;
        if (accPart.toolInvocation?.toolCallId === toolCallId) return accPart;
        if (accPart.subAgentBuffer) {
          const nested = this.findToolInvocationByCallId(accPart.subAgentBuffer, toolCallId);
          if (nested) return nested;
        }
      }
    }
    return undefined;
  }

  private upsertToolPart(buffer: MessageBuffer, upsert: ToolPartUpsert): void {
    const message = this.lastAssistantMessage(buffer);
    const parts = (message.parts ??= []);
    const existingPart = parts.find(
      (part): part is ToolPart =>
        part.type === 'tool-invocation' && part.toolInvocation?.toolCallId === upsert.toolCallId,
    );
    const prev = existingPart?.toolInvocation;
    const prevResult = prev?.state === 'result' ? prev.result : undefined;
    const toolName = upsert.toolName || prev?.toolName || '';
    const args = upsert.args === undefined ? prev?.args : upsert.args;
    const invocation: ToolPart['toolInvocation'] =
      upsert.state === 'result'
        ? {
            state: 'result',
            toolCallId: upsert.toolCallId,
            toolName,
            args,
            result: upsert.result === undefined ? prevResult : upsert.result,
          }
        : {
            state: upsert.state,
            toolCallId: upsert.toolCallId,
            toolName,
            args,
          };
    if (existingPart) {
      existingPart.toolInvocation = invocation;
      if (upsert.providerMetadata !== undefined) {
        existingPart.providerMetadata = upsert.providerMetadata;
      }
      return;
    }
    const part: ToolPart = {
      type: 'tool-invocation',
      toolInvocation: invocation,
      ...(upsert.providerMetadata !== undefined
        ? { providerMetadata: upsert.providerMetadata }
        : {}),
    };
    parts.push(part);
  }
}
